import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { generateCardData, DASHBOARD } from "../../constants/dashboardConstants";
import DashboardCard from './DashboardCard';
import DashboardUpcomingTrip from './DashboardUpcomingTrip.jsx';
import config from "../../config.json";
import './Dashboard.css';
import {
    getUserAlerts,
    getVehicleAlerts,
    getExpiryAlerts,
} from "../../pages/alerts/AlertsApis.js";
import { useTranslation } from 'react-i18next';
import { useUser } from "../../pages/login/UserContextPersistent";

/**
 * DashboardDatas Component
 * Displays an overview of organization's vehicles, routes, stops
 * Fetches data from multiple endpoints and displays it in a grid layout
 */
export default function DashboardDatas() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { state } = useUser();
    const currentLanguage = i18n.language;

    // State Management
    const [overview, setOverview] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        vehicles: [],
        routes: [],
        stops: [],
    });
    const [alerts, setAlerts] = useState({
        userAlerts: [],
        vehicleAlerts: [],
        expiryAlerts: []
    });
    
    // Key-based permission map
    const [cardKeyMap, setCardKeyMap] = useState({});

    // Define permission map
    const cardPermissionMap = {
        vehicles: { path: "/api/vehicle", action: "GET" },
        routes: { path: "/api/route", action: "GET" },
        stops: { path: "/api/stop", action: "GET" },
        alerts: { path: "/api/alert", action: "GET" },
        drivers: { path: "/api/driver", action: "GET" },
        sites: { path: "/api/site", action: "GET" }
    };

    /**
     * Checks if the user has the required permission
     * @param {string} requiredPath - API path to check
     * @param {string} requiredAction - Action type (GET, POST, etc.)
     * @returns {boolean} - Whether the user has permission
     */
    const hasPermission = (requiredPath, requiredAction = null) => {
        // If no features found or no path required, allow access
        if (!state?.features || !requiredPath) {
            return true;
        }

        // Check if the user has the required permission
        return state.features.some(feature => {
            // Check if the API path matches
            const pathMatches = feature.api_path.includes(requiredPath);

            // If action is specified, check that too, otherwise just check the path
            return requiredAction ?
                (pathMatches && feature.action === requiredAction) :
                pathMatches;
        });
    };

    /**
     * Stores the original card IDs and their permission types before translation
     */
    const storeOriginalCardKeys = () => {
        // Create a map of original card data before translation
        const englishCardData = generateCardData(overview, (key) => key);
        const newCardKeyMap = {};
        
        englishCardData.forEach(card => {
            // Store card.id -> permission type mapping
            for (const [permType, _] of Object.entries(cardPermissionMap)) {
                if (card.title.toLowerCase().includes(permType.toLowerCase())) {
                    newCardKeyMap[card.id] = permType;
                    break;
                }
            }
        });
        
        setCardKeyMap(newCardKeyMap);
    };

    /**
     * Filters cards based on user permissions using card IDs instead of titles
     * @param {Array} cardsData - Array of card data objects
     * @returns {Array} - Filtered array of cards
     */
    const filterCardsByPermission = (cardsData) => {
        if (!cardsData || !Array.isArray(cardsData)) return [];
        
        return cardsData.filter(card => {
            // Look up the permission type using the card's ID
            const permissionType = cardKeyMap[card.id];
            
            // If no specific permission type found, show the card
            if (!permissionType) return true;
            
            // Get the permission requirements for this card type
            const permission = cardPermissionMap[permissionType];
            
            // If no permission configuration, show the card
            if (!permission) return true;
            
            // Check against user permissions
            return hasPermission(permission.path, permission.action);
        });
    };

    // API Calls
    /**
     * Fetches all vehicles for the organization
     * @returns {Array} Array of vehicle objects
     */
    const getAllVehicles = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/vehicle/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    /**
     * Fetches all routes for the organization
     * @returns {Array} Array of route objects
     */
    const getAllRoutes = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/route/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    /**
     * Fetches all stops for the organization
     * @returns {Array} Array of stop objects
     */
    const getAllStops = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/stop/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    /**
     * Fetches all alerts for the organization
     * @returns {Object} Object containing all types of alerts
     */
    const getAllAlerts = async () => {
        try {
            const [userAlerts, vehicleAlerts, expiryAlerts] = await Promise.all([
                getUserAlerts(),
                getVehicleAlerts(),
                getExpiryAlerts()
            ]);
            return {
                userAlerts,
                vehicleAlerts,
                expiryAlerts
            };
        } catch (error) {
            handleApiError(error);
            return {
                userAlerts: [],
                vehicleAlerts: [],
                expiryAlerts: []
            };
        }
    };

    /**
     * Handles API errors consistently across all API calls
     * @param {Error} error - The error object from the API call
     */
    const handleApiError = (error) => {
        console.error('API Error:', error);
        if (error.response?.status === 401) {
            navigate("/");
        }
    };

    /**
     * Processes raw data and updates the overview state
     * @param {Object} rawData - Object containing all fetched data
     * @param {Object} alertsData - Object containing all fetched alerts
     */
    const processOverviewData = (rawData, alertsData) => {
        const { vehicles, routes, stops } = rawData;
        const { userAlerts, vehicleAlerts, expiryAlerts } = alertsData;
        const totalAlerts = userAlerts.length + vehicleAlerts.length + expiryAlerts.length;

        setOverview({
            vehicles: vehicles.length,
            routes: routes.length,
            stops: stops.length,
            activeVehicles: vehicles.filter(v => v.status === "ACTIVE").length,
            inactiveVehicles: vehicles.filter(v => v.status === "INACTIVE").length,
            deadVehicles: vehicles.filter(v => v.status === "DEAD").length,
            totalAlerts: totalAlerts,
            vehicleAlerts: vehicleAlerts.length,
            userAlerts: userAlerts.length,
            expiryAlerts: expiryAlerts.length,
        });
    };

    // Data Fetching Effect
    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [vehicles, routes, stops, alertsData] = await Promise.all([
                    getAllVehicles(),
                    getAllRoutes(),
                    getAllStops(),
                    getAllAlerts()
                ]);

                if (isMounted) {
                    const newData = { vehicles, routes, stops };
                    setData(newData);
                    setAlerts(alertsData);
                    processOverviewData(newData, alertsData);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);  // Only run on mount, not on language change

    // Effect to store original card keys when overview data changes
    useEffect(() => {
        if (Object.keys(overview).length > 0) {
            storeOriginalCardKeys();
        }
    }, [overview]);

    // Generate and filter card data with memoization for better performance
    const filteredCards = useMemo(() => {
        const cardData = generateCardData(overview, t);
        return filterCardsByPermission(cardData);
    }, [overview, t, currentLanguage, state?.features, cardKeyMap]);

    // Render dashboard card skeletons when loading
    const renderCardSkeletons = () => {
        return Array(6).fill(0).map((_, index) => (
            <div key={`skeleton-card-${index}`} className="dashboard-card">
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" height={30} />
                <Skeleton variant="rectangular" width="100%" height={120} sx={{ mt: 2 }} />
            </div>
        ));
    };

    // Render upcoming trip skeleton when loading
    const renderUpcomingTripSkeleton = () => (
        <div className="dashboard-upcoming-section">
            <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={200} />
        </div>
    );

    // Render stylish empty state when no permissions
    const renderNoPermissionsState = () => (
        <>
        
        </>
    );

    // Render
    return (
        <div className='dashboard-container'>
            <span className='dashboard-title'>{t(DASHBOARD)}</span>
            <div className="row">
                {isLoading ? (
                    <>
                        <div className="dashboard-grid-container">
                            {renderCardSkeletons()}
                        </div>
                        {renderUpcomingTripSkeleton()}
                    </>
                ) : (
                    <>
                        {filteredCards.length > 0 ? (
                            <>
                                <div className="dashboard-grid-container">
                                    {filteredCards.map((card) => (
                                        <DashboardCard key={card.id} {...card} />
                                    ))}
                                </div>

                                <div className="dashboard-upcoming-section">
                                    <DashboardUpcomingTrip />
                                </div>
                            </>
                        ) : (
                            renderNoPermissionsState()
                        )}
                    </>
                )}
            </div>
        </div>
    );
}